import { Fragment, useEffect, useMemo, useState } from 'react'
import { useAppSelector } from '../../ReduxToolkit/Hooks';
import { MenuList } from '../../Data/LayoutData/SidebarData';
import Menulist from './Menulist';
import { MenuItem } from '../../Types/Layout/SidebarType';
import { H6, LI } from '../../AbstractElements';
import { useTranslation } from 'react-i18next';
import { hasFinanceAccess, hasOutSourcingAnalyticsAccess, isOutSourcingUser, isSalesAgentUser, isSalesRestrictedUser, OUT_SOURCING_MENU_ITEMS, PROFILE_DATA_UPDATED_EVENT, SALES_MENU_TITLES, SALES_TELEPHONY_ITEMS, useTelephonyOnlyUser } from '../../utils/permissions';
import { hasEmailAccess, useEmailAccessConfig } from '../../utils/emailAccessConfig';
import { isTelephonyAdmin } from '../../Telephony/roles';
import { RM_TASKS_ENABLED } from '../../Tasks/tasksApi';

const DEMO_SHIFT_ACTIVE_TITLE = "Demo Shift Active";

// "LiveDemoAccessOnly" users in adminEmailAccess.json keep their role menu and also get Demo Ops → Demo Operations
const DEMO_OPS_TITLE = "Demo Ops";
const DEMO_OPERATIONS_TITLE = "Demo Operations";

// Menu items that require finance permission to be visible
const FINANCE_MENU_ITEMS = ["Transactions", "Admin Teacher Reports"];

// Telephony submenus visible to non-admin roles (dialing lives in the bubble).
// Analytics is open to every admin-panel role; non-admins see only their own calls
// (scope 'self', see getTelephonyAnalyticsScope). Outsourced agents are on
// "outSourcingAnalytics" via OUT_SOURCING_MENU_ITEMS.
const TELEPHONY_NON_ADMIN_ITEMS = ["Logs", "Countries", "Analytics"];

// Telephony-only users ("telephonyOnly" in adminEmailAccess.json) get just these; matches isTelephonyOnlyPath
const TELEPHONY_ONLY_ITEMS = ["Logs", "Countries"];

// RM/Tasks menus — hidden entirely while REACT_APP_RM_TASKS_ENABLED is off
// (beta/prod, until this branch's rollout is ready there).
// They all live in Consumer Xperience → RM, so hiding the "RM" group hides them all.
const RM_TASKS_MENU_ITEMS = ["RM"];

/** Hidden from SALES_AGENT (SALES_ADMIN still sees them). */
const isSalesAgentHiddenChild = (title: string): boolean => {
  const key = title.toLowerCase().replace(/[^a-z0-9]/g, "");
  return key === "salesagents" || key === "salesagent" || key === "targetsincentives";
};

const SidebarMenuList = () => {
    const [activeMenu, setActiveMenu] = useState<string[]>([]);
    const { pinedMenu } = useAppSelector((state) => state.layout);
    const { t } = useTranslation();
    const shouldHideMenu = (mainMenu: MenuItem) => {return mainMenu?.Items?.map((data) => data.title).every((titles) =>pinedMenu.includes(titles || ""));};
    const telephonyOnly = useTelephonyOnlyUser();
    const emailAccessVersion = useEmailAccessConfig();
    const [profileVersion, setProfileVersion] = useState(0);

    useEffect(() => {
      const refresh = () => setProfileVersion((version) => version + 1);
      window.addEventListener(PROFILE_DATA_UPDATED_EVENT, refresh);
      return () => window.removeEventListener(PROFILE_DATA_UPDATED_EVENT, refresh);
    }, []);

    const roleMenuList = useMemo(() => {
      const financeAccess = hasFinanceAccess();
      const telephonyAdmin = isTelephonyAdmin();
      // Outsourced agents (ROLE_OUT_SOURCING) see only Profiles → Student and Telephony → Logs/Countries,
      // plus Telephony → Analytics when their email is on "outSourcingAnalytics"
      if (isSalesRestrictedUser()) {
        const hideAgentItems = isSalesAgentUser();
        return MenuList.map((mainMenu) => ({
          ...mainMenu,
          Items: mainMenu.Items?.filter((item) => SALES_MENU_TITLES.includes(item.title || "")).map((item) => ({
            ...item,
            children: item.title === "Telephony"
              ? item.children?.filter((child) => SALES_TELEPHONY_ITEMS.includes(child.title || ""))
              : hideAgentItems
              ? item.children?.filter((child) => !isSalesAgentHiddenChild(child.title || ""))
              : item.children,
          })),
        })).filter((mainMenu) => (mainMenu.Items?.length ?? 0) > 0);
      }
      if (isOutSourcingUser()) {
        const analyticsAccess = hasOutSourcingAnalyticsAccess();
        return MenuList.map((mainMenu) => ({
          ...mainMenu,
          Items: mainMenu.Items?.filter((item) => !!OUT_SOURCING_MENU_ITEMS[item.title || ""]).map((item) => ({
            ...item,
            children: item.children?.filter((child) =>
              OUT_SOURCING_MENU_ITEMS[item.title || ""].includes(child.title || "") ||
              (analyticsAccess && item.title === "Telephony" && child.title === "Analytics"),
            ),
          })),
        })).filter((mainMenu) => (mainMenu.Items?.length ?? 0) > 0);
      }
      if (financeAccess && telephonyAdmin && RM_TASKS_ENABLED && !telephonyOnly) return MenuList;
      return MenuList.map((mainMenu) => ({
        ...mainMenu,
        Items: mainMenu.Items?.filter(
          (item) => financeAccess || !FINANCE_MENU_ITEMS.includes(item.title || ""),
        ).filter(
          // Telephony-only users ("telephonyOnly" in adminEmailAccess.json) see just the Telephony menu
          (item) => !telephonyOnly || item.title === "Telephony",
        ).map((item) => {
          // Telephony-only users get the non-admin submenus even if their role is ROLE_ADMIN
          if (item.title === "Telephony" && (!telephonyAdmin || telephonyOnly)) {
            return {
              ...item,
              children: item.children?.filter((child) =>
                (telephonyOnly ? TELEPHONY_ONLY_ITEMS : TELEPHONY_NON_ADMIN_ITEMS).includes(child.title || ""),
              ),
            };
          }
          if (!RM_TASKS_ENABLED && item.children?.some((child) => RM_TASKS_MENU_ITEMS.includes(child.title || ""))) {
            return {
              ...item,
              children: item.children?.filter(
                (child) => !RM_TASKS_MENU_ITEMS.includes(child.title || ""),
              ),
            };
          }
          return item;
        }),
      })).filter((mainMenu) => !telephonyOnly || (mainMenu.Items?.length ?? 0) > 0);
    }, [telephonyOnly, emailAccessVersion, profileVersion]);

    // "Demo Shift Active" is shown only to emails on the "demoShiftActive" list in adminEmailAccess.json
    const filteredMenuList = useMemo(() => {
      const showDemoShiftActive = hasEmailAccess("demoShiftActive");
      const hide = (items?: MenuItem[]): MenuItem[] | undefined =>
        items
          ?.filter((item) => showDemoShiftActive || item.title !== DEMO_SHIFT_ACTIVE_TITLE)
          .map((item) => (item.children ? { ...item, children: hide(item.children) } : item));
      const visible = roleMenuList.map((mainMenu) => ({ ...mainMenu, Items: hide(mainMenu.Items) }));
      if (!hasEmailAccess("LiveDemoAccessOnly")) return visible;
      // Add Demo Operations (Live Demo) from the full MenuList when the role menu doesn't already have it
      const hasDemoOperations = visible.some((mainMenu) =>
        mainMenu.Items?.some((item) => item.title === DEMO_OPS_TITLE && item.children?.some((child) => child.title === DEMO_OPERATIONS_TITLE)),
      );
      if (hasDemoOperations) return visible;
      const demoOpsItems = (mainMenu: MenuItem): MenuItem[] =>
        (mainMenu.Items || [])
          .filter((item) => item.title === DEMO_OPS_TITLE)
          .map((item) => ({ ...item, children: item.children?.filter((child) => child.title === DEMO_OPERATIONS_TITLE) }));
      const merged = visible.map((mainMenu) => {
        const source = MenuList.find((m) => m.title === mainMenu.title);
        return source && demoOpsItems(source).length ? { ...mainMenu, Items: [...(mainMenu.Items || []), ...demoOpsItems(source)] } : mainMenu;
      });
      const placed = merged.some((mainMenu) => mainMenu.Items?.some((item) => item.title === DEMO_OPS_TITLE));
      if (placed) return merged;
      const fallback = MenuList.find((m) => demoOpsItems(m).length);
      return fallback ? [...merged, { ...fallback, Items: demoOpsItems(fallback) }] : merged;
    }, [roleMenuList, emailAccessVersion, profileVersion]);

    return (
      <>
        {filteredMenuList &&
          filteredMenuList.map((mainMenu: MenuItem, index) => (
            <Fragment key={index}>
              <LI>
                <div>
                  <H6 className={mainMenu.lanClass && mainMenu.lanClass}>{(mainMenu.title)}</H6>
                </div>
              </LI>
              <Menulist menu={mainMenu.Items} activeMenu={activeMenu} setActiveMenu={setActiveMenu}  level={0}/>
            </Fragment>
          ))}
      </>
    );
}

export default SidebarMenuList